// src/context/AppContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
} from 'firebase/auth';
import {
  doc, setDoc, getDoc, collection,
  addDoc, serverTimestamp,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import toast from 'react-hot-toast';

const AppContext = createContext();

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};

// ── Validate email format ─────────────────────────────────────────
export function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

// ── Map Firebase error codes to user-friendly messages ───────────
function authErrorMsg(code) {
  switch (code) {
    case 'auth/user-not-found':       return 'No account found with this email.';
    case 'auth/wrong-password':       return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use': return 'This email is already registered. Please login.';
    case 'auth/weak-password':        return 'Password must be at least 6 characters.';
    case 'auth/invalid-email':        return 'Please enter a valid email address.';
    case 'auth/too-many-requests':    return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed': return 'Network error. Check your internet connection.';
    case 'auth/invalid-credential':   return 'Invalid email or password. Please check and try again.';
    case 'auth/user-disabled':        return 'This account has been disabled. Contact support.';
    default:                          return 'Something went wrong. Please try again.';
  }
}

export const AppProvider = ({ children }) => {
  const [user,     setUser]    = useState(null);
  const [loading,  setLoading] = useState(true);
  const [cart,     setCart]    = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);

  // ── Auth state listener ─────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
          const data = snap.exists() ? snap.data() : {};
          setUser({ ...firebaseUser, ...data });

          // Restore cart & wishlist from localStorage
          const savedCart     = localStorage.getItem(`cart_${firebaseUser.uid}`);
          const savedWishlist = localStorage.getItem(`wishlist_${firebaseUser.uid}`);
          if (savedCart)     setCart(JSON.parse(savedCart));
          if (savedWishlist) setWishlist(JSON.parse(savedWishlist));
        } catch (err) {
          console.error('Failed to load user profile:', err);
          setUser(firebaseUser);
        }
      } else {
        setUser(null);
        const guestCart = localStorage.getItem('cart_guest');
        if (guestCart) setCart(JSON.parse(guestCart));
        setWishlist([]);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // ── Persist cart ────────────────────────────────────────────────
  useEffect(() => {
    const key = user ? `cart_${user.uid}` : 'cart_guest';
    localStorage.setItem(key, JSON.stringify(cart));
  }, [cart, user]);

  // ── Persist wishlist ─────────────────────────────────────────────
  useEffect(() => {
    if (user?.uid) {
      localStorage.setItem(`wishlist_${user.uid}`, JSON.stringify(wishlist));
    }
  }, [wishlist, user]);

  // ── Refresh user role from Firestore (call after role changes) ──
  const refreshUser = useCallback(async () => {
    if (!auth.currentUser) return;
    try {
      const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
      if (snap.exists()) {
        setUser((prev) => ({ ...prev, ...snap.data() }));
      }
    } catch (err) {
      console.error('refreshUser error:', err);
    }
  }, []);

  // ── Sign up ──────────────────────────────────────────────────────
  const signup = async (email, password, displayName) => {
    if (!isValidEmail(email)) throw new Error('Please enter a valid email address.');
    if (!displayName?.trim()) throw new Error('Please enter your full name.');
    if (password?.length < 6) throw new Error('Password must be at least 6 characters.');

    const { user: newUser } = await createUserWithEmailAndPassword(auth, email.trim(), password);
    await updateProfile(newUser, { displayName: displayName.trim() });

    // Send email verification
    try { await sendEmailVerification(newUser); } catch {}

    await setDoc(doc(db, 'users', newUser.uid), {
      uid:         newUser.uid,
      email:       email.trim().toLowerCase(),
      displayName: displayName.trim(),
      role:        'user',
      createdAt:   serverTimestamp(),
    });
    toast.success(`Welcome to CoupleCart, ${displayName.trim()}! 💕`);
  };

  // ── Login ────────────────────────────────────────────────────────
  const login = async (email, password) => {
    if (!isValidEmail(email)) throw new Error('Please enter a valid email address.');
    if (!password)            throw new Error('Please enter your password.');
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      toast.success('Welcome back! 💕');
    } catch (err) {
      throw new Error(authErrorMsg(err.code));
    }
  };

  // ── Logout ───────────────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setCart([]);
    setWishlist([]);
    toast.success('Logged out successfully');
  };

  // ── Cart ─────────────────────────────────────────────────────────
  const addToCart = useCallback((product, quantity = 1) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        toast.success('Quantity updated in cart');
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      toast.success(`${product.name} added to cart 🛒`);
      return [...prev, { ...product, quantity }];
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
    toast.success('Item removed from cart');
  }, []);

  const updateCartQuantity = useCallback((productId, quantity) => {
    if (quantity < 1) return;
    setCart((prev) =>
      prev.map((item) => item.id === productId ? { ...item, quantity } : item)
    );
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // ── Wishlist ─────────────────────────────────────────────────────
  const toggleWishlist = useCallback((product) => {
    if (!user) { toast.error('Please login to save to wishlist'); return; }
    setWishlist((prev) => {
      const exists = prev.find((item) => item.id === product.id);
      if (exists) {
        toast.success('Removed from wishlist');
        return prev.filter((item) => item.id !== product.id);
      }
      toast.success('Added to wishlist 💖');
      return [...prev, product];
    });
  }, [user]);

  const isInWishlist = useCallback(
    (productId) => wishlist.some((item) => item.id === productId),
    [wishlist]
  );

  // ── Place order (legacy - for simple flows) ──────────────────────
  const placeOrder = async (orderData) => {
    if (!user) throw new Error('Must be logged in to place order');
    const ref = await addDoc(collection(db, 'orders'), {
      ...orderData,
      userId:    user.uid,
      userEmail: user.email,
      status:    'confirmed',
      createdAt: serverTimestamp(),
    });
    clearCart();
    return ref.id;
  };

  const value = {
    user, loading, signup, login, logout, refreshUser,
    cart, cartTotal, cartCount, cartOpen, setCartOpen,
    addToCart, removeFromCart, updateCartQuantity, clearCart,
    wishlist, toggleWishlist, isInWishlist,
    placeOrder,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
